/** Which specialists the Manager may reach in a conversation.
 *
 * Shared by the project's Conversations tab and the agent-level one, so the two
 * cannot disagree about what "all of them" means. The Manager is always in it and
 * is not listed: it is the one answering.
 *
 * `null` is "the whole team", which is what the server reads an absent field as
 * and what every conversation started before this got. An empty array is the
 * Manager alone - a different, deliberate choice, so the two are kept apart all
 * the way to the row. See docs/Y2026-Q3/design-operator-scoped-agent-runs.md.
 */
import type { AgentPromptSummary } from "../api/hooks/useStatefulAgents";
import styles from "./SpecialistPicker.module.css";

export const SpecialistPicker = ({
  roster,
  chosen,
  onChange,
}: {
  /** Every project agent, Manager included - it is filtered out here. */
  roster: readonly AgentPromptSummary[];
  chosen: string[] | null;
  onChange: (next: string[] | null) => void;
}) => {
  const specialists = roster.filter((a) => !a.is_manager);
  if (specialists.length < 2) return null;
  const all = specialists.map((a) => a.agent);
  const ticked = chosen ?? all;

  const toggle = (agent: string) => {
    const next = ticked.includes(agent)
      ? ticked.filter((a) => a !== agent)
      : all.filter((a) => ticked.includes(a) || a === agent);
    // Back to everything ticked is back to "unrestricted" - the same request the
    // page makes when nobody has touched this control.
    onChange(next.length === all.length ? null : next);
  };

  return (
    <div className={styles.picker}>
      <span className={styles.label}>The Manager can bring in</span>
      {specialists.map((a) => (
        <label key={a.agent} className={styles.choice} title={a.description}>
          <input
            type="checkbox"
            checked={ticked.includes(a.agent)}
            onChange={() => toggle(a.agent)}
          />
          {a.label}
        </label>
      ))}
      {chosen !== null && (
        <button type="button" className={styles.reset} onClick={() => onChange(null)}>
          all of them
        </button>
      )}
    </div>
  );
};
